import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";
import { useSelector } from "react-redux";
import { List, useDynamicRowHeight } from "react-window";
import toast from "react-hot-toast";
import {
    useTheme,
    Box,
    Typography,
    Popover,
    Tooltip,
    Tabs,
    Tab,
    ToggleButton,
} from "@mui/material";
import { Sort } from "@mui/icons-material";
import {
    OTP_DELAY_MINUTES,
    DIVERT_RETURN_STATUS,
} from "state/constants";
import api from "state/axios";
import { splitString, formatTZDate } from "state/helpers";
import { fetchLegMembersByFlt } from "state/api";
import { hasPermission } from "state/checkPermission";
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import CrewListDialog from "components/CrewListDialog";


const DELAY_KEYS = {
    depDelays: {
        time: "depDelaysTime",
        codes: "depDelaysCode",
    },
    arrDelays: {
        time: "arrDelaysTime",
        codes: "arrDelaysCode",
    },
    diverts: {
        time: "depDelaysTime",
        codes: "depDelaysCode",
    },
};

/* ===================== Delay Popover ===================== */
const DelayPopover = ({ anchorEl, onClose, description }) => (
    <Popover
        container={document.body}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
        <Typography p={1}>{description}</Typography>
    </Popover>
);


/* ===================== Row Component ===================== */
const RowComponent = React.memo(function RowComponent({
    index,
    style,
    timeMode,
    theme,
    canView,
    flights,
    delayType,
    handleViewRosters,
    handlePopoverOpen,
    setPopoverInfo,
}) {
    const flight = flights?.[index];
    if (!flight) return null;
    
    const formatDate = (d) => formatTZDate(d, 'UTC', "dd/MM");
    const getTime = (flight, field) => timeMode === "UTC"
        ? flight[field]
        : flight[`${field}LocalBase`];

    const delayTime = flight[DELAY_KEYS[delayType].time];
    const delayCodes = flight[DELAY_KEYS[delayType].codes];


    return (
        <div style={style}>
            <FlexBetween
                px={1}
                py={0.5}
                gap={0.5}
                borderBottom={`1px solid ${theme.palette.neutral.dark}`}
            >
                <Box width="50px">
                    <Typography>
                        {formatDate(getTime(flight, "flightDate"))}
                    </Typography>

                    {/* <Tooltip title={canView ? "Click to view crew list" : ""} arrow> */}
                        <Typography
                            color={theme.palette.primary.dark}
                            sx={{
                                textDecoration: canView ? "underline" : "none",
                                cursor: canView ? "pointer" : "text",
                            }}
                            onClick={ canView
                                ? () =>
                                    handleViewRosters(
                                        flight.flightDate,
                                        flight.flightDesc,
                                        flight.dep
                                    )
                                : undefined
                            }
                        >
                            {flight.flightDesc || flight.flightNo}
                        </Typography>
                    {/* </Tooltip> */}
                </Box>

                <Box width="30px" textAlign="center">
                    <Typography>{flight.dep}</Typography>
                    <Typography>{flight.arr}</Typography>
                </Box>

                <Box width="36px" textAlign="center">
                    <Typography>{flight.acType || ''}</Typography>
                    <Typography>
                        {splitString(flight.flightReg, "-", 1) || flight.flightReg}
                    </Typography>
                </Box>

                <Box width="35px" textAlign="center">
                    <Typography>
                        {getTime(flight, "std")}
                    </Typography>
                    <Typography>
                        {getTime(flight, "sta")}
                    </Typography>
                </Box>

                <Box width="35px" textAlign="center" color={theme.palette.primary.dark}>
                    <Typography>
                        {getTime(flight, "atd")}
                    </Typography>
                    <Typography>
                        {getTime(flight, "ata")}
                    </Typography>
                </Box>

                <Box width="60px" textAlign="center">
                    <Typography
                        color={theme.palette.primary.dark}
                        sx={
                            flight.remarks
                                ? { textDecoration: "underline", cursor: "pointer" }
                                : {}
                        }
                        onClick={(e) => flight.remarks &&
                            setPopoverInfo({
                                anchorEl: e.currentTarget,
                                description: flight.remarks,
                            })
                        }
                    >
                        {`${delayTime}'`}
                    </Typography>

                    <FlexCenter gap={0.5}>
                        {(delayCodes || "")
                            ?.split(" ")
                            ?.map((delayIndex, i) => (
                                <Typography
                                    key={i}
                                    sx={{ textDecoration: "underline", cursor: "pointer" }}
                                    onClick={(e) => handlePopoverOpen(e, delayIndex)}
                                >
                                    {delayIndex}
                                </Typography>
                            )
                        )}
                    </FlexCenter>
                </Box>
            </FlexBetween>
        </div>
    );
});


const useResizeObserver = (
    ref,
    callback,
    delay = 100 
) => {
    const timeoutRef = useRef(null);
    const frameRef = useRef(null);
    const lastRectRef = useRef(null);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;

            lastRectRef.current = entry.contentRect;
            if (timeoutRef.current) return;

            timeoutRef.current = setTimeout(() => {
                frameRef.current = requestAnimationFrame(() => {
                    callback(lastRectRef.current);
                });

                timeoutRef.current = null;
            }, delay);
        });

        observer.observe(ref.current);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutRef.current);
            cancelAnimationFrame(frameRef.current);
        };
    }, [ref, callback, delay]);
};


/* ===================== Main Component ===================== */
const FlightDelayList = ({ flights = [] }) => {
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const canView = hasPermission(user?.permissions, "MainPage", "view", false);

    const listRef = useRef(null);
    const ROW_HEIGHT = 48; 
    const [listHeight, setListHeight] = useState(500);
    useResizeObserver(listRef, (rect) => {
        setListHeight(rect.height - ROW_HEIGHT);
    }, 100);

    /* -------- react-window -------- */
    const rowHeight = useDynamicRowHeight({
        defaultRowHeight: ROW_HEIGHT,
    });

    const [sortMode, setSortMode] = useState("default");
    const [delayType, setDelayType] = useState("depDelays");
    const [delayCodes, setDelayCodes] = useState({});
    const [popoverInfo, setPopoverInfo] = useState({
        anchorEl: null,
        description: "",
    });

    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);

    /* -------- Fetch delay codes -------- */
    useEffect(() => { api.get('/delaycodes')
        .then((res) => setDelayCodes(res.data || {}))
        .catch((e) =>
            toast.error(e?.message || "Failed to fetch delay codes!")
        );
    }, []);

    const handlePopoverOpen = useCallback((e, delayIndex) => {
        if (!e?.stopPropagation) return;
        e.stopPropagation();

        setPopoverInfo({
            anchorEl: e.currentTarget,
            description: delayCodes?.[delayIndex]?.description || "No descriptions available",
        });
    }, [delayCodes]);

    const handleViewRosters = useCallback(async (flightDate, flightDesc, dep) => {
        if (!canView) return;

        const { 
            flightDetails, 
            legMembers, 
            jumpSeaters 
        } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);

        setFlight(flightDetails);
        setCrewList(legMembers);
        setJumpList(jumpSeaters);
        setOpenCrewListDialog(true);
    }, [canView]);

    const filteredFlights = useMemo(() => {
        const base = flights.filter((f) => {
            if (!f) return false;

            if (delayType === "depDelays")
                return (
                    !DIVERT_RETURN_STATUS.includes(f.flightStatus) &&
                    f.depDelaysTime > OTP_DELAY_MINUTES
                );

            if (delayType === "arrDelays")
                return (
                    !DIVERT_RETURN_STATUS.includes(f.flightStatus) &&
                    f.arrDelaysTime > OTP_DELAY_MINUTES
                );

            return DIVERT_RETURN_STATUS.includes(f.flightStatus);
        });

        if (sortMode === "delayTime") {
            const key = DELAY_KEYS[delayType].time;

            return [...base].sort((a, b) =>
                (b?.[key] || 0) - (a?.[key] || 0)
            );
        }

        // ===== default sort (giữ nguyên thứ tự backend) =====
        return base;

    }, [flights, delayType, sortMode]);

    const delayCounts = useMemo(() => {
        const counts = {
            depDelays: 0,
            arrDelays: 0,
            diverts: 0
        };

        flights?.forEach(f => {
            if (!f) return;

            if (!DIVERT_RETURN_STATUS.includes(f?.flightStatus) && f?.depDelaysTime > OTP_DELAY_MINUTES) {
                counts.depDelays++;
            }
            if (!DIVERT_RETURN_STATUS.includes(f?.flightStatus) && f?.arrDelaysTime > OTP_DELAY_MINUTES) {
                counts.arrDelays++;
            }
            if (DIVERT_RETURN_STATUS.includes(f?.flightStatus)) {
                counts.diverts++;
            }
        });

        return counts;
    }, [flights]);

    
    const rowProps = useMemo(() => ({
        timeMode,
        theme,
        canView,
        flights: filteredFlights,
        delayType,
        handleViewRosters,
        handlePopoverOpen,
        setPopoverInfo,
    }), [
        timeMode,
        theme,
        canView,
        filteredFlights,
        delayType,
        handleViewRosters,
        handlePopoverOpen
    ]);


    return (
        <Box
            ref={listRef}
            sx={{
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                overflowX: 'hidden',
            }}
        >
            <Box
                zIndex={3}
                position="sticky"
                top={0}            
                borderBottom={`1px solid ${theme.palette.neutral.deep}`}
                bgcolor={theme.palette.background.default}                
            >                
                <FlexBetween>
                    <Tabs 
                        value={delayType} 
                        onChange={(_, v) => setDelayType(v)}
                        // textColor="inherit" 
                        // sx={{
                        //     '& .MuiTab-root': {
                        //         color: theme.palette.text.secondary,
                        //     },
                        //     '& .Mui-selected': {
                        //         color: theme.palette.text.primary, 
                        //     },
                        // }}
                    >
                        <Tab value="depDelays" label={`Dep Delays (${delayCounts.depDelays})`} />
                        <Tab value="arrDelays" label={`Arr Delays (${delayCounts.arrDelays})`} />
                        <Tab value="diverts" label={`Diverts (${delayCounts.diverts})`} />
                    </Tabs>

                     <Tooltip title={sortMode === "default" ? "Sort by Std" : "Sort by Delay Time"} arrow>
                        <ToggleButton
                            size="small"
                            value="delaySort"
                            selected={sortMode === "delayTime"}
                            onChange={() =>
                                setSortMode(prev =>
                                    prev === "default" ? "delayTime" : "default"
                                )
                            }
                        >
                            <Sort />
                        </ToggleButton>
                    </Tooltip>
                </FlexBetween>
            </Box>
            
            <List
                height={listHeight}
                rowCount={filteredFlights.length}
                rowHeight={rowHeight}
                rowComponent={RowComponent}
                rowProps={rowProps}
            />

            <DelayPopover
                anchorEl={popoverInfo.anchorEl}
                onClose={() =>
                    setPopoverInfo({ anchorEl: null, description: "" })
                }
                description={popoverInfo.description}
            />

            {openCrewListDialog && (
                <CrewListDialog
                    open={openCrewListDialog}
                    onClose={() => setOpenCrewListDialog(false)}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            )}
        </Box>
    );
};

export default FlightDelayList;
